import 'react-toastify/ReactToastify.css';

import { Alert, AlertTitle, SnackbarOrigin, styled } from '@mui/material';
import React, { ReactNode, createContext, useContext } from 'react';
import { ToastContainer, toast } from 'react-toastify';

import { ValidationErrorsList } from './components/ValidationErrorsList';
import { formatError } from '@/common/application/utils/errorFormatter';

type SnackbarType = 'success' | 'info' | 'warning' | 'error';

interface ValidationErrorDetail {
  path?: string;
  reason?: string;
  field?: string;
  message?: string;
}

interface SnackbarOptions {
  anchorOrigin?: SnackbarOrigin;
  duration?: number | null | false;
  variant?: 'filled' | 'outlined' | 'standard';
  action?: ReactNode;
  validationErrors?: Array<ValidationErrorDetail>;
}

const SnackbarContainer = styled(ToastContainer)({
  '.Toastify__toast': {
    padding: 0,
    width: 'max-content'
  },
  '.Toastify__close-button': {
    display: 'none'
  }
});

interface SnackbarItem {
  id: number;
  title: string;
  message: string;
  type: SnackbarType;
  options: SnackbarOptions;
}

export interface SnackbarContextType {
  showSnackbar: (title: string, message: string, type?: SnackbarType, options?: SnackbarOptions) => number;
  closeSnackbar: (id: number) => void;
  showErrorWithDetails: (error: unknown, defaultMessage?: string, defaultTitle?: string) => number;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

interface SnackbarProviderProps {
  children?: ReactNode;
}

// Wrapper component to filter out react-toastify props that shouldn't be passed to Alert
interface ToastAlertWrapperProps {
  title: string;
  message: string;
  type: SnackbarType;
  variant?: 'filled' | 'outlined' | 'standard';
  action?: ReactNode;
  hasValidationErrors: boolean;
  validationErrors?: Array<ValidationErrorDetail>;
  onClose: () => void;
  // These props come from react-toastify but should be ignored
  closeToast?: unknown;
  toastProps?: unknown;
  isPaused?: unknown;
}

const ToastAlertWrapper: React.FC<ToastAlertWrapperProps> = ({
  title,
  message,
  type,
  variant,
  action,
  hasValidationErrors,
  validationErrors,
  onClose,
  ...rest // Capture and ignore react-toastify props (closeToast, toastProps, isPaused, etc.)
}) => {
  // Explicitly ignore rest props to prevent them from being passed to Alert
  void rest;
  return (
    <Alert
      onClose={onClose}
      severity={type}
      variant={variant ?? 'standard'}
      action={action}
      sx={{
        ...(hasValidationErrors && {
          '& .MuiAlert-message': {
            width: '100%'
          }
        })
      }}
    >
      <AlertTitle>{title}</AlertTitle>
      {message}
      {hasValidationErrors && <ValidationErrorsList validationErrors={validationErrors!} />}
    </Alert>
  );
};

const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
  const closeSnackbar = (id: number) => {
    toast.dismiss(id);
  };

  const showSnackbar = (title: string, message: string, type: SnackbarType = 'info', options: SnackbarOptions = {}) => {
    const newSnackbar: SnackbarItem = {
      id: Date.now(),
      title,
      message,
      type,
      options: {
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
        duration: options.duration ?? 4000,
        ...options
      }
    };

    const hasValidationErrors = !!(options.validationErrors && options.validationErrors.length > 0);

    toast(
      <ToastAlertWrapper
        title={newSnackbar.title}
        message={newSnackbar.message}
        type={newSnackbar.type}
        variant={newSnackbar.options.variant}
        action={newSnackbar.options.action}
        hasValidationErrors={hasValidationErrors}
        validationErrors={options.validationErrors}
        onClose={() => closeSnackbar(newSnackbar.id)}
      />,
      {
        toastId: newSnackbar.id,
        position: newSnackbar.options.anchorOrigin
          ? `${newSnackbar.options.anchorOrigin?.vertical}-${newSnackbar.options.anchorOrigin?.horizontal}`
          : 'bottom-right',
        autoClose: newSnackbar.options.duration ?? 4000,
        hideProgressBar: true
      }
    );

    return newSnackbar.id;
  };

  const showErrorWithDetails = (error: unknown, defaultMessage?: string, defaultTitle?: string) => {
    const formatted = formatError(error, defaultMessage, defaultTitle);
    return showSnackbar(formatted.title, formatted.message, 'error', {
      validationErrors: formatted.details
    });
  };

  const contextValue = { showSnackbar, closeSnackbar, showErrorWithDetails };

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <SnackbarContainer />
    </SnackbarContext.Provider>
  );
};

export default SnackbarProvider;
